import { useNavigate, Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useCoffee } from "@/context/CoffeeContext";
import { CoffeeModal } from "@/components/CoffeeModal";
import { NotificationBell } from "@/components/NotificationBell";
import { CalculatorWidget } from "@/components/CalculatorWidget";
import { BTN_PRIMARY, BTN_GHOST, ICON_BTN } from "@/lib/styles";
import {
  Smartphone, LogOut, CalendarClock, Sun, Moon, FolderOpen, Coffee, Home,
  CalendarCheck, Menu as MenuIcon, ChevronDown, Download, Trash2, Shield, PiggyBank, GraduationCap, HardDrive,
} from "lucide-react";

const MENU_ITEMS = [
  { to: "/mis-archivos", label: "Mis Archivos", Icon: HardDrive, testid: "menu-mis-archivos" },
  { to: "/documentos", label: "Documentos", Icon: FolderOpen, testid: "menu-documents" },
  { to: "/finanzas", label: "Gastos", Icon: PiggyBank, testid: "menu-finanzas" },
  { to: "/agenda", label: "Agenda", Icon: CalendarCheck, testid: "menu-agenda" },
  { to: "/tutoriales", label: "Tutoriales", Icon: GraduationCap, testid: "menu-tutoriales" },
  { to: "/bitacora", label: "Bitácora", Icon: CalendarClock, testid: "menu-bitacora" },
  { to: "/link", label: "Vincular dispositivo", Icon: Smartphone, testid: "menu-link-device" },
  { to: "/papelera", label: "Papelera", Icon: Trash2, testid: "menu-trash" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { status } = useCoffee();
  const [coffeeOpen, setCoffeeOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installHelp, setInstallHelp] = useState(false);
  const [tourControlled, setTourControlled] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const level = status?.level ?? 0;
  const dot = level === 0 ? "bg-emerald-500" : level === 1 ? "bg-amber-500" : level === 2 ? "bg-orange-500" : "bg-rose-500";

  useEffect(() => {
    if (window.__deferredPrompt) setDeferredPrompt(window.__deferredPrompt);
    const onInstallable = () => setDeferredPrompt(window.__deferredPrompt);
    const handler = (e) => { e.preventDefault(); window.__deferredPrompt = e; setDeferredPrompt(e); };
    const onInstalled = () => setDeferredPrompt(null);
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  useEffect(() => {
    const onTourMenu = (e) => {
      const open = !!e.detail;
      setTourControlled(open);
      setMenuOpen(open);
    };
    window.addEventListener("notacora-tour-menu", onTourMenu);
    return () => window.removeEventListener("notacora-tour-menu", onTourMenu);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    if (menuOpen && !tourControlled) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen, tourControlled]);

  const go = (to) => { setMenuOpen(false); navigate(to); };

  const install = async () => {
    setMenuOpen(false);
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      window.__deferredPrompt = null;
      setDeferredPrompt(null);
      return;
    }
    setInstallHelp(true);
  };

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);
  const isIOS = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <nav
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800 h-16 flex items-center justify-between px-3 md:px-8 gap-2"
      data-testid="navbar"
    >
      <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
        <img src="/icon-192.png" alt="Notacora" className="w-8 h-8 rounded-lg" />
        <span className="font-heading font-semibold text-lg tracking-tight hidden xs:inline sm:inline">Notacora</span>
      </Link>

      <div className="flex items-center gap-1 md:gap-1.5">
        <button onClick={() => navigate("/")} className={`${ICON_BTN} text-xs`} data-testid="navbar-home" title="Inicio">
          <Home size={18} />
          <span className="hidden md:inline">Inicio</span>
        </button>

        {/* Menú desplegable en columna */}
        <div className="relative" ref={menuRef}>
          <button onClick={() => setMenuOpen((o) => !o)} className={`${ICON_BTN} text-xs`} data-testid="navbar-menu-toggle" title="Menú">
            <MenuIcon size={18} />
            <span className="hidden md:inline">Menú</span>
            <ChevronDown size={14} className={`transition-transform ${menuOpen ? "rotate-180" : ""}`} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl p-1.5 flex flex-col z-50" data-testid="navbar-menu">
              {MENU_ITEMS.map(({ to, label, Icon, testid }) => (
                <button key={to} onClick={() => go(to)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left" data-testid={testid}>
                  <Icon size={17} className="text-indigo-500" /> {label}
                </button>
              ))}
              {status?.is_admin && (
                <>
                  <div className="h-px bg-slate-200 dark:bg-slate-800 my-1" />
                  <button onClick={() => go("/admin")} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors text-left font-medium" data-testid="menu-admin">
                    <Shield size={17} /> Administración
                  </button>
                </>
              )}
              {!isStandalone && (
                <>
                  <div className="h-px bg-slate-200 dark:bg-slate-800 my-1" />
                  <button onClick={install} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-colors text-left" data-testid="menu-install">
                    <Download size={17} /> Instalar app
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <button onClick={toggle} className={ICON_BTN} data-testid="theme-toggle" title="Cambiar tema">
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <NotificationBell />
        <button onClick={() => setCoffeeOpen(true)} className={`${ICON_BTN} relative text-xs`} data-testid="navbar-coffee" title="Tus cafés">
          <Coffee size={18} className="text-amber-500" />
          {status && <span className="hidden sm:inline font-mono2">{status.months_left}</span>}
          <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${dot}`} />
        </button>
        <button onClick={() => setConfirmLogout(true)} className={ICON_BTN} data-testid="navbar-logout" title="Cerrar sesión">
          <LogOut size={18} />
        </button>
      </div>

      {confirmLogout && (
        <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmLogout(false)} data-testid="logout-confirm">
          <div className="w-full max-w-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-2xl bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto mb-4"><LogOut size={22} /></div>
            <h3 className="font-heading font-medium text-lg mb-1">¿Cerrar sesión?</h3>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-5">Tendrás que iniciar sesión de nuevo para volver a entrar.</p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => setConfirmLogout(false)} className={BTN_GHOST} data-testid="logout-no">No</button>
              <button onClick={() => { setConfirmLogout(false); logout(); }} className={BTN_PRIMARY} data-testid="logout-yes">Sí, salir</button>
            </div>
          </div>
        </div>
      )}

      {installHelp && (
        <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setInstallHelp(false)} data-testid="install-help">
          <div className="w-full max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mb-4"><Download size={22} /></div>
            <h3 className="font-heading font-medium text-lg mb-1">Instalar Notacora</h3>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4">
              Tu navegador no mostró la instalación automática. Sigue estos pasos para instalarla como app:
            </p>
            {isIOS ? (
              <ol className="font-body text-sm text-slate-700 dark:text-slate-200 space-y-2 list-decimal pl-5">
                <li>Toca el botón <b>Compartir</b> (el cuadro con la flecha hacia arriba).</li>
                <li>Elige <b>"Agregar a pantalla de inicio"</b>.</li>
                <li>Toca <b>Agregar</b>.</li>
              </ol>
            ) : (
              <ol className="font-body text-sm text-slate-700 dark:text-slate-200 space-y-2 list-decimal pl-5">
                <li>Abre el menú del navegador (los <b>⋮</b> arriba a la derecha).</li>
                <li>Elige <b>"Instalar app"</b> o <b>"Agregar a pantalla de inicio"</b>.</li>
                <li>Confirma la instalación.</li>
              </ol>
            )}
            <p className="font-body text-xs text-slate-400 mt-4">Si ya la tienes instalada, ábrela desde el ícono de Notacora en tu dispositivo.</p>
            <div className="flex justify-end mt-5">
              <button onClick={() => setInstallHelp(false)} className={BTN_PRIMARY} data-testid="install-help-close">Entendido</button>
            </div>
          </div>
        </div>
      )}

      {user && <span className="sr-only" data-testid="navbar-user-email">{user.email}</span>}
      <CoffeeModal open={coffeeOpen} onClose={() => setCoffeeOpen(false)} />
      <CalculatorWidget />
    </nav>
  );
}
